// src/modules/documents/hooks/useDocumentMove.js
//
// Ctrl+M tuşuyla aktive olan doc M-drag özelliği.
//
// Davranış:
// - Ctrl+M basılı + mousedown documentGroup'a → drag başlar.
// - Sürükleme sırasında mouse Y world koordinatına göre target slot index
//   hesaplanır (aynı grup içinde). Slot değişirse:
//     • Runtime order ref güncellenir (Redux'a yazılmaz — sadece live preview)
//     • Sadece o grubun doc'larının pozisyonları Three.js'te yeniden hesaplanır
//       (layout effect TETİKLENMEZ → "rerender olmasın" şartı)
// - Sürüklenen doc mouse pozisyonuna yapışır (ghost preview).
// - Mouseup (veya Ctrl+M bırak) → final order Redux'a dispatch → layout effect
//   bir kez clean final state için tetiklenir.
//
// Drag kapsamı:
// - Pin'li doc'lar (DEFAULT_PINNED_DOCS) drag yapamaz.
// - Dinamik doc'lar (Kat Planı, Vaziyet Planı, Ground Floor) drag dışı.
// - Sürükleme sadece grup içi sıra değişimi (gruplar arası KAPSAM DIŞI).

import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { useSelector, useDispatch } from 'react-redux';
import { throttleRAF } from '../../../utils/throttle';
import {
  selectManualGroupOrder,
  setManualGroupOrder,
} from '../../../redux/documentLayoutSlice';
import {
  DOC_NAME_TO_GROUP,
  DEFAULT_PINNED_DOCS,
  FALLBACK_LAYOUT_GROUP,
} from '../../../constants/projectTypes';
import { tryPlaceInCol } from '../layout/columnPacking';

// Global mode flag — diğer hook'lar (örn. selection) okuyabilir gerekirse
export const documentMoveModeRef = { current: false };

// Sürüklenen doc'un slot pos'unda görünecek dashed outline klavuz dörtgeni.
// Doc sol-üst origin'inden (0,0) sağ alta (-h) uzanır — documentGroup
// koordinat sistemi ile uyumlu (Y aşağı pozitif değil, Y pozitif yukarı).
function createGuideRect(w, h) {
  const points = [
    new THREE.Vector3(0, 0, 0),
    new THREE.Vector3(w, 0, 0),
    new THREE.Vector3(w, -h, 0),
    new THREE.Vector3(0, -h, 0),
    new THREE.Vector3(0, 0, 0),
  ];
  const geom = new THREE.BufferGeometry().setFromPoints(points);
  const mat = new THREE.LineDashedMaterial({
    color: 0x3b82f6,
    dashSize: 30,
    gapSize: 20,
    transparent: true,
    opacity: 0.9,
  });
  const line = new THREE.Line(geom, mat);
  line.computeLineDistances();
  line.userData.isDocDragGuide = true;
  line.renderOrder = 999;
  return line;
}

const DYNAMIC_DOC_NAMES = new Set(['Kat Planı', 'Vaziyet Planı', 'Ground Floor']);

function isPinnedName(name, settings, projectMeta) {
  const pinned = Array.isArray(projectMeta?.pinnedDocs)
    ? projectMeta.pinnedDocs
    : Array.isArray(settings?.pinnedDocs)
      ? settings.pinnedDocs
      : DEFAULT_PINNED_DOCS;
  return new Set(pinned).has(name);
}

function isDraggable(name, settings, projectMeta) {
  if (!name) return false;
  if (DYNAMIC_DOC_NAMES.has(name)) return false;
  if (isPinnedName(name, settings, projectMeta)) return false;
  return true;
}

// Sürüklenen doc'un mouse delta'sına göre yeni global slot'unu hesaplar.
// dragCtx: { startCursorY, origFlatIdx, totalDocs, stepY, flatOrder }
// localPos: cursor mainFrameGroup-local pozisyonu
// Dönüş: { newOrder, targetGlobalSlot } veya null (slot değişmedi)
function computeReorderedByDelta(dragCtx, localPos, lastGlobalSlot) {
  if (!dragCtx) return null;
  const deltaY = localPos.y - dragCtx.startCursorY;
  // Negatif Y = aşağı (Three.js Y pozitif yukarı). Yani:
  // mouse YUKARI gittiyse cursor.y arttı → deltaY > 0 → slotDelta < 0 → slot azalır
  // mouse AŞAĞI gittiyse cursor.y azaldı → deltaY < 0 → slotDelta > 0 → slot artar
  const slotDelta = -Math.round(deltaY / dragCtx.stepY);
  const targetGlobalSlot = Math.max(
    0,
    Math.min(dragCtx.totalDocs - 1, dragCtx.origFlatIdx + slotDelta),
  );
  if (targetGlobalSlot === lastGlobalSlot) return null; // değişiklik yok

  // flatOrder'a draggedDoc'u targetGlobalSlot'a splice et
  const finalOrder = [...dragCtx.flatOrder];
  // flatOrder length = totalDocs - 1 (sürüklenen hariç). splice index 0..length OK.
  const splicePos = Math.min(targetGlobalSlot, finalOrder.length);
  finalOrder.splice(splicePos, 0, dragCtx.docName);
  return { newOrder: finalOrder, targetGlobalSlot };
}

// Aynı grupta hangi sütunda ve sütun içinde hangi slot'a düşülecek hesabı.
// state: layoutStateRef.current
// docName: sürüklenen doc adı
// worldPos: mouse world pozisyonu {x, y}
// Dönüş: { groupCols } (yeni groupCols snapshot'ı — sürüklenen doc'un yeni
// pozisyonda yer aldığı versiyon) veya null (değişiklik yok)
// eslint-disable-next-line no-unused-vars
function computeReorderedGroup(state, draggedDocName, draggedGroupKey, worldPos) {
  if (!state) return null;
  const cols = state.groupCols[draggedGroupKey];
  if (!Array.isArray(cols) || cols.length === 0) return null;

  // Bu grupta sürüklenmeyen tüm doc'ları topla (rect'leriyle birlikte).
  // Doc rect'i: { x: col.x + d.subX, y: col.y - d.subY (üst kenar), w, h }
  // useElectricalLayout subY: doc'un üst kenarından sütunun üstüne mesafe.
  // World y: col.y - d.subY (üst), col.y - d.subY - d.h (alt).
  const candidates = [];
  cols.forEach((col, colIdx) => {
    col.docs.forEach((d) => {
      if (d.docDef?.name === draggedDocName) return; // kendi rect'ini sayma
      candidates.push({
        colIdx,
        col,
        topY: (col.y ?? 0) - d.subY,
        bottomY: (col.y ?? 0) - d.subY - d.h,
        leftX: (col.x ?? 0) + d.subX,
        rightX: (col.x ?? 0) + d.subX + d.w,
        d,
      });
    });
  });

  // Hangi sütun: mouse'un X'i en yakın hangi sütunun X-aralığında?
  // (Sütunlar aynı grupta yan yana yerleşik, her birinin maxWidth'i var.)
  let targetColIdx = -1;
  let bestColDist = Infinity;
  cols.forEach((col, idx) => {
    const cLeft = col.x ?? 0;
    const cRight = (col.x ?? 0) + (col.maxWidth || 0);
    // Mouse sütunun içinde mi?
    if (worldPos.x >= cLeft && worldPos.x <= cRight) {
      targetColIdx = idx;
      bestColDist = 0;
      return;
    }
    const dist = Math.min(Math.abs(worldPos.x - cLeft), Math.abs(worldPos.x - cRight));
    if (dist < bestColDist) { bestColDist = dist; targetColIdx = idx; }
  });
  if (targetColIdx < 0) return null;

  // Hedef sütundaki doc'lar (sürüklenen hariç) topY sırasına göre dizili olur.
  // Slot 0 = en üst, slot N = en alt.
  const colDocs = cols[targetColIdx].docs
    .filter((d) => d.docDef?.name !== draggedDocName)
    .map((d) => ({ d, topY: (cols[targetColIdx].y ?? 0) - d.subY, bottomY: (cols[targetColIdx].y ?? 0) - d.subY - d.h }))
    .sort((a, b) => b.topY - a.topY); // topY büyükten küçüğe = yukarıdan aşağıya

  // Slot tespiti: GAP-BASED (kullanıcının istediği yeni UX)
  // - Cursor EN ÜST doc'un yukarısında (gap 0) → slot 0
  // - Cursor doc[i-1] alt kenarı ile doc[i] üst kenarı ARASINDA (gap i) → slot i
  // - Cursor EN ALT doc'un aşağısında (gap N) → slot N
  // - Cursor BİR DOC'UN İÇİNDE → slot değişmez (null döndür, reposition YOK)
  //
  // Bu sayede kullanıcı cursor'u doc'lar üzerinden geçirirken reposition
  // gürültüsü olmaz; sadece "boşluk"a girdiğinde diğer doc'lar açılır.
  let slot = null;
  if (colDocs.length === 0) {
    slot = 0;
  } else if (worldPos.y > colDocs[0].topY) {
    slot = 0; // en üst gap
  } else if (worldPos.y < colDocs[colDocs.length - 1].bottomY) {
    slot = colDocs.length; // en alt gap
  } else {
    for (let i = 0; i < colDocs.length - 1; i += 1) {
      // doc[i] alt kenarı ile doc[i+1] üst kenarı arası gap
      if (worldPos.y <= colDocs[i].bottomY && worldPos.y >= colDocs[i + 1].topY) {
        slot = i + 1;
        break;
      }
    }
  }
  if (slot === null) return null; // cursor bir doc içinde → değişiklik yok

  // Yeni order: bu grup içindeki tüm doc'ların yeni sıralanmış listesi.
  // Sütun bazlı düşünüyoruz ama Redux state grup başına flat liste tutuyor.
  // Çözüm: önce mevcut grup doc'larını flat order'a aç (sütun sırası ×
  // sütun içi sıra), sonra sürüklenen doc'u hedef pozisyona ekle.
  const flatOrder = [];
  cols.forEach((col) => {
    const colSorted = [...col.docs]
      .filter((d) => d.docDef?.name !== draggedDocName)
      .sort((a, b) => a.subY - b.subY); // küçük subY = üst sırada
    colSorted.forEach((d) => flatOrder.push(d.docDef.name));
  });
  // Hedef sütundaki "slot" pozisyonu: bu sütunun flat order'daki ofset'i +
  // sütun içi slot index'i.
  let targetFlatIndex = 0;
  for (let i = 0; i < targetColIdx; i += 1) {
    targetFlatIndex += cols[i].docs.filter((d) => d.docDef?.name !== draggedDocName).length;
  }
  targetFlatIndex += slot;
  const finalOrder = [...flatOrder];
  finalOrder.splice(targetFlatIndex, 0, draggedDocName);
  return { newOrder: finalOrder, targetColIdx, targetSlot: slot };
}

// Sürükleme sırasında live preview yapar — layout effect TETİKLENMEZ.
// Sadece SÜRÜKLENEN GRUBUN sütunlarını yeniden hesaplar (diğer gruplara
// dokunmaz). compactColumn `position.set(0,0,0)` ile bbox ölçtüğü için
// her frame'de doc'lar bir an mainFrame'in sol-üst köşesine "pop" eder;
// tüm gruplara çağırırsak TÜM doc'lar pop atar → kullanıcı "üste yapışıyor"
// gözlemi. Sadece sürüklenen grupta yapınca pop o grupla sınırlı, diğerleri
// yerinde kalır.
function repositionGroup(state, groupKey, newOrder) {
  if (!state || !state.groupCols) return;
  const cols = state.groupCols[groupKey];
  if (!Array.isArray(cols) || cols.length === 0) return;

  const maxColH = state.maxColH;
  const DOC_OFFSET = state.DOC_OFFSET;
  const layoutSettings = state.layoutSettings || {};

  // 1. Sürüklenen grubun sütunlarını newOrder'a göre yeniden inşa
  // (tryPlaceInCol greedy — sıra değişimi otomatik sütun atamasını üretir).
  const recordsByName = new Map();
  cols.forEach((c) => c.docs.forEach((d) => recordsByName.set(d.docDef.name, d)));
  const newCols = [];
  newOrder.forEach((name) => {
    const rec = recordsByName.get(name);
    if (!rec) return;
    let chosenCol = newCols[newCols.length - 1];
    let chosenPos = chosenCol ? tryPlaceInCol(chosenCol, rec.w, rec.h, maxColH, DOC_OFFSET) : null;
    if (!chosenPos) {
      chosenCol = { x: 0, height: 0, maxWidth: 0, docs: [] };
      newCols.push(chosenCol);
      chosenPos = tryPlaceInCol(chosenCol, rec.w, rec.h, maxColH, DOC_OFFSET) || { subX: 0, subY: 0 };
    }
    chosenCol.docs.push({ ...rec, subX: chosenPos.subX, subY: chosenPos.subY });
    chosenCol.height = Math.max(chosenCol.height, chosenPos.subY + rec.h);
    chosenCol.maxWidth = Math.max(chosenCol.maxWidth, chosenPos.subX + rec.w);
  });

  // 2. Eski sütun X ve Y pozisyonlarını koru (yatay sıra değişmez).
  // Sütun sayısı azaldıysa eski X'leri sırayla kullan; arttıysa son sütunun
  // sağına aritmetik ekleme yap.
  const oldXs = cols.map((c) => c.x ?? 0);
  const oldYs = cols.map((c) => c.y ?? 0);
  newCols.forEach((nc, idx) => {
    nc.x = idx < oldXs.length
      ? oldXs[idx]
      : (oldXs[oldXs.length - 1] || 0) + (cols[cols.length - 1]?.maxWidth || 0) + DOC_OFFSET;
    nc.y = idx < oldYs.length ? oldYs[idx] : oldYs[oldYs.length - 1] || 0;
  });

  // 3. positionColumns'un basit pos mantığı (columnPacking.js:53-58):
  // doc.position = (col.x + subX, -DOC_OFFSET - subY - shift). VA='bottom'
  // ise shift = free, doc'lar sütun altına dayanır.
  //
  // compactColumn KULLANILMAZ çünkü `position.set(0,0,0)` adımıyla her
  // mousemove'da pop yapıyor → kullanıcı "doc'lar üste yapışıyor" diyor.
  // compactColumn topOffset (bbox.max.y) ile contentGroup içeriğini hizalar
  // — bu mantık layout effect raf-defer'de uygulanır. Live preview'de aynı
  // basit positionColumns mantığını kullanırsak görsel "bottom" sağlanır;
  // mouseup'tan sonra layout effect compactColumn ile içerik-hizalı son
  // pos'a getirir.
  const VA = layoutSettings.verticalAlign || 'bottom';
  newCols.forEach((col) => {
    const free = maxColH - col.height;
    const shift = VA === 'bottom' ? free : VA === 'center' ? free / 2 : 0;
    col.docs.forEach((d) => {
      if (!d.group) return;
      d.group.position.set(
        (col.x ?? 0) + d.subX,
        -DOC_OFFSET - d.subY - (Math.abs(shift) > 0.01 ? shift : 0),
        0,
      );
    });
  });

  // Live state'i güncelle (sonraki mousemove için)
  state.groupCols[groupKey] = newCols;
}

export default function useDocumentMove(scene, camera, renderer, layoutStateRef) {
  const dispatch = useDispatch();
  const manualGroupOrder = useSelector(selectManualGroupOrder);
  const settings = useSelector((s) => s.settings);
  const projectMeta = useSelector((s) => s.project?.projectMetadata);

  // Sürükleme runtime state
  const draggingRef = useRef(null);
  // { docName, groupKey, group, startWorldPos, currentOrder, draggedRecOriginal }

  // Manuel order ref — her render'da güncel kopya, mouseup'ta dispatch için
  const manualOrderRef = useRef(manualGroupOrder);
  useEffect(() => { manualOrderRef.current = manualGroupOrder; }, [manualGroupOrder]);

  useEffect(() => {
    if (!scene || !camera || !renderer) return;
    const dom = renderer.domElement;
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();

    const getWorldPoint = (event) => {
      const rect = dom.getBoundingClientRect();
      mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(mouse, camera);
      const plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
      const point = new THREE.Vector3();
      raycaster.ray.intersectPlane(plane, point);
      return { x: point.x, y: point.y, world: point.clone() };
    };

    // mainFrameGroup'un kendi world pozisyonu sıfır değilse doc koord'ları
    // (col.y, d.subY → mainFrameGroup LOCAL) ile cursor WORLD koord uyumsuz
    // olur → slot karşılaştırması her zaman aynı yanlış slot'u döner.
    // Bu fonksiyon world point'i mainFrameGroup local'ine çevirir.
    const toMainFrameLocal = (worldPoint, mainFrameGroup) => {
      if (!mainFrameGroup) return { x: worldPoint.x, y: worldPoint.y };
      const local = mainFrameGroup.worldToLocal(worldPoint.clone());
      return { x: local.x, y: local.y };
    };

    // Sahnedeki bir mesh'in atalarında userData.docName'i bul
    const findDocGroup = (obj) => {
      let cur = obj;
      while (cur) {
        if (cur.userData?.docName) return cur;
        cur = cur.parent;
      }
      return null;
    };

    const onKeyDown = (e) => {
      if (e.key === 'm' && (e.ctrlKey || e.metaKey)) {
        documentMoveModeRef.current = true;
        e.preventDefault?.();
      }
    };
    const onKeyUp = (e) => {
      if (e.key === 'm' || e.key === 'M' || e.key === 'Control' || e.key === 'Meta') {
        documentMoveModeRef.current = false;
      }
    };

    const onMouseDown = (event) => {
      if (!documentMoveModeRef.current) return;
      if (draggingRef.current) return;
      const state = layoutStateRef?.current;
      if (!state || !state.mainFrameGroup) return;

      // Raycast → sahnedeki documentGroup'u bul
      const worldPos = getWorldPoint(event);
      const intersects = raycaster.intersectObjects(state.mainFrameGroup.children, true);
      const hit = intersects.find((i) => findDocGroup(i.object));
      if (!hit) return;
      const docGroup = findDocGroup(hit.object);
      const docName = docGroup.userData?.docName;
      if (!isDraggable(docName, settings, projectMeta)) return;

      const groupKey = DOC_NAME_TO_GROUP[docName] || FALLBACK_LAYOUT_GROUP;
      event.stopPropagation();
      event.preventDefault();

      // Sürüklenen doc'un w/h değerlerini state'ten al → klavuz dörtgen boyutu
      const draggedRec = state.groupCols?.[groupKey]
        ?.flatMap((c) => c.docs)
        ?.find((d) => d.docDef?.name === docName);
      const guideW = draggedRec?.w || draggedRec?.docDef?.w || 100;
      const guideH = draggedRec?.h || 100;

      // Klavuz dörtgen oluştur — mainFrameGroup içine ekle ki sürüklenen
      // doc'la aynı koord sisteminde yaşasın. Görünmez başlar; ilk slot
      // tespitinden sonra görünür olur.
      const guide = createGuideRect(guideW, guideH);
      guide.visible = false;
      state.mainFrameGroup.add(guide);

      // Delta-based slot için context: tüm grup doc'larını flat-order'da
      // topla (sütun sırası × her sütunda subY ascending). Sürüklenen doc
      // dahil sıralı liste; flatOrder ise sürüklenen hariç. Mouse delta
      // yukarı (cursor.y artar) → slot index azalır → doc üst slotlara taşınır.
      const allDocsFlat = [];
      (state.groupCols?.[groupKey] || []).forEach((col) => {
        const sorted = [...col.docs].sort((a, b) => a.subY - b.subY);
        sorted.forEach((d) => {
          if (d.docDef?.name) allDocsFlat.push({ name: d.docDef.name, h: d.h });
        });
      });
      const origFlatIdx = allDocsFlat.findIndex((x) => x.name === docName);
      const avgH = allDocsFlat.length > 0
        ? allDocsFlat.reduce((s, d) => s + (d.h || 200), 0) / allDocsFlat.length
        : 200;
      // Bir slot adım için cursor'un kat etmesi gereken Y mesafesi.
      // avgH + offset → "komşu doc'un orta noktasına ulaşacak" mesafe.
      const stepY = Math.max(80, (avgH || 200) + (state.DOC_OFFSET || 50));
      const flatOrder = allDocsFlat.filter((x) => x.name !== docName).map((x) => x.name);

      // Local pos için cursor'un mainFrameGroup-local Y'sine ihtiyaç var.
      const worldVec = new THREE.Vector3(worldPos.x, worldPos.y, 0);
      const localStart = state.mainFrameGroup
        ? state.mainFrameGroup.worldToLocal(worldVec.clone())
        : { y: worldPos.y };

      draggingRef.current = {
        docName,
        groupKey,
        group: docGroup,
        startWorldPos: worldPos,
        origPosition: docGroup.position.clone(),
        guide,
        lastGlobalSlot: origFlatIdx, // başlangıçta kendi slot'unda
        dragCtx: {
          docName,
          startCursorY: localStart.y,
          origFlatIdx: origFlatIdx >= 0 ? origFlatIdx : 0,
          totalDocs: allDocsFlat.length,
          stepY,
          flatOrder,
        },
      };
    };

    const onMouseMove = (event) => {
      if (!draggingRef.current) return;
      const state = layoutStateRef?.current;
      if (!state) return;
      const worldPos = getWorldPoint(event);
      const { groupKey, group, guide, dragCtx } = draggingRef.current;

      // Cursor'u mainFrameGroup local koord'ına çevir — ghost ve delta hesabı
      // için bu space gerekli (doc'lar bu space'te yaşıyor).
      const localPos = toMainFrameLocal(worldPos.world, state.mainFrameGroup);

      // Ghost (sürüklenen doc) cursor'a yapışır (sol-üst origin)
      group.position.set(localPos.x, localPos.y, 0.5);

      // Delta-based slot tespiti: cursor'un dragStart'tan farkı stepY birimine
      // bölünüp slot adımına çevrilir. Sahnenin boyutundan bağımsız → kullanıcı
      // küçük mouse hareketi ile bile slot değiştirebilir.
      const reorder = computeReorderedByDelta(
        dragCtx,
        localPos,
        draggingRef.current.lastGlobalSlot,
      );
      if (!reorder) return; // slot değişmedi

      draggingRef.current.lastReorder = reorder;
      draggingRef.current.lastGlobalSlot = reorder.targetGlobalSlot;
      repositionGroup(state, groupKey, reorder.newOrder);

      // Sürüklenen doc repositionGroup tarafından yeni slot pozisyonuna
      // yerleştirildi → bu pos klavuz pozisyonu. Ghost'u tekrar cursor'a
      // yapıştırmadan ÖNCE klavuza kopyala.
      if (guide) {
        guide.position.copy(group.position);
        guide.visible = true;
      }
      group.position.set(localPos.x, localPos.y, 0.5);
    };
    const throttledMouseMove = throttleRAF(onMouseMove);

    const onMouseUp = () => {
      if (!draggingRef.current) return;
      const state = layoutStateRef?.current;
      const { groupKey, guide } = draggingRef.current;

      // Klavuz dörtgen temizle
      if (guide && guide.parent) {
        guide.parent.remove(guide);
        guide.geometry?.dispose?.();
        guide.material?.dispose?.();
      }

      if (state) {
        // Mevcut grupCols sırası → flat order, Redux'a yaz
        const cols = state.groupCols[groupKey] || [];
        const flatOrder = [];
        cols.forEach((col) => {
          const sorted = [...col.docs].sort((a, b) => a.subY - b.subY);
          sorted.forEach((d) => { if (d.docDef?.name) flatOrder.push(d.docDef.name); });
        });
        if (flatOrder.length > 0) {
          dispatch(setManualGroupOrder({ groupKey, order: flatOrder }));
        }
      }
      draggingRef.current = null;
    };

    dom.addEventListener('mousedown', onMouseDown, true);
    dom.addEventListener('mousemove', throttledMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);

    return () => {
      throttledMouseMove.cancel?.();
      // Sürükleme aktifken unmount olduysa klavuz dörtgeni temizle
      const g = draggingRef.current?.guide;
      if (g && g.parent) {
        g.parent.remove(g);
        g.geometry?.dispose?.();
        g.material?.dispose?.();
      }
      draggingRef.current = null;
      dom.removeEventListener('mousedown', onMouseDown, true);
      dom.removeEventListener('mousemove', throttledMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [scene, camera, renderer, layoutStateRef, dispatch, settings, projectMeta]);
}
